import { useAsync } from 'react-use';

import { GroupEntity } from '@backstage/catalog-model';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  fetchUserEmail,
  normalizeTeamEntityRef,
} from '../home/action-required/utils';
import { isUserManagerOfTeam } from '../home/hooks/useManagedTeamWithChildren';

/**
 * Determines whether the current user may trigger entity providers.
 *
 * Rule: outside production anyone may trigger; in production only users who are
 * a **member** OR the **manager** of a group listed in the
 * `app.frontstageAdminGroups` config may. Managers frequently have no `memberOf`
 * relationship with their team (so they're absent from `ownershipEntityRefs`),
 * hence the separate manager check against each group's `metadata.managers`.
 * The provider list itself is always readable — this only gates the Trigger
 * action.
 */
export function useCanTriggerProviders(): {
  canTrigger: boolean;
  loading: boolean;
} {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const isProd = configApi.getOptionalString('auth.environment') === 'prod';

  const { value: isAdmin, loading } = useAsync(async () => {
    if (!isProd) {
      return true;
    }

    const adminGroups = (
      configApi.getOptionalStringArray('app.frontstageAdminGroups') ?? []
    ).map(normalizeTeamEntityRef);

    if (adminGroups.length === 0) {
      return false;
    }

    // Member of an allowed group?
    const { ownershipEntityRefs = [] } =
      await identityApi.getBackstageIdentity();
    if (ownershipEntityRefs.some(ref => adminGroups.includes(ref))) {
      return true;
    }

    // Manager of an allowed group? A manager often has no `memberOf` edge to
    // the team, so compare their email against each group's managers list.
    const userEmail = await fetchUserEmail(identityApi, catalogApi);
    if (userEmail === 'unknown') {
      return false;
    }

    const managerChecks = await Promise.all(
      adminGroups.map(async ref => {
        try {
          const group = (await catalogApi.getEntityByRef(ref)) as
            | GroupEntity
            | undefined;
          return group ? isUserManagerOfTeam(group, userEmail) : false;
        } catch {
          return false;
        }
      }),
    );
    return managerChecks.some(Boolean);
  }, [isProd]);

  // Non-prod is permitted synchronously, so the button is enabled immediately
  // rather than flickering while the (unused) membership check resolves.
  return {
    canTrigger: !isProd || Boolean(isAdmin),
    loading: isProd ? loading : false,
  };
}
